import { api } from "@/api";
import { authSession } from "@/utils/authSession";
import { useEffect } from "react";

const REVALIDATE_INTERVAL_MS = 60_000;

/**
 * Cobre o usuário que ficou com a aba parada: ao voltar o foco, dispara um
 * request autenticado barato. Quem decide se a sessão está desatualizada é o
 * backend — se as permissões mudaram (ou o usuário foi desativado) desde o
 * último request, o PermissionsVersionMiddleware responde 401 stale_token e o
 * interceptor do axios renova o token em silêncio, recarregando a página
 * quando o conjunto de features mudou. Se nada mudou, o ping é um 200 e nada
 * acontece.
 *
 * Não força refresh direto de propósito: cada refresh rotaciona o refresh
 * token, e rotacionar a cada troca de aba multiplicaria a chance de corrida
 * entre abas pelo token rotativo.
 */
export const usePermissionsRevalidation = () => {
  useEffect(() => {
    // Começa "vencido" de propósito: o primeiro retorno de foco já revalida
    // (inicializar com Date.now() suprimiria qualquer ping nos primeiros
    // 60s de vida do app).
    let lastCheckAt = 0;

    const revalidate = () => {
      if (document.visibilityState !== "visible") return;
      if (!authSession.hasSession()) return;
      if (Date.now() - lastCheckAt < REVALIDATE_INTERVAL_MS) return;

      lastCheckAt = Date.now();

      // Erros são ignorados: o efeito útil (refresh/reload/logout) acontece
      // dentro do interceptor; falha de rede aqui não deve incomodar o uso.
      api.get("v1/users/features").catch(() => {});
    };

    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);

    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
    };
  }, []);
};
